import { useEffect, useState } from 'react';
import { ExternalLink } from '../../components/Form/Button';
import { useConfig } from '../../config';
import { useTranslation } from '../../i18n/i18n';
import { useApi } from '../../state/api';
import { Res } from '../../state/api-resources';
import { ApiProductPage } from '../../types';
import { ContentCard } from '../ContentCard/ContentCard';
import { Icon, close, play } from '../Icon/Icon';
import { Loader } from '../Loader/Loader';
import { Modal } from '../Modal/Modal';
import { EmbeddedYoutubeVideo } from '../EmbeddedYoutubeVideo/EmbeddedYoutubeVideo';
import { DescriptionVideo, youtubeVideo } from './descriptionVideos';
import './ProductInformation.css';

export function ProductInformation({ productData: productPageData }: { productData: ApiProductPage }) {
	const { vendorProductId, links, tcId } = productPageData;
	const t = useTranslation().product;
	const { store } = useConfig();
	const [showProducerModal, setShowProducerModal] = useState(false);
	// a youtube link among the documents, Emo's "Video", plays here instead of leaving the page
	const [video, setVideo] = useState<DescriptionVideo | null>(null);
	// the router reuses this component between products, so an open modal must not follow along
	useEffect(() => {
		setVideo(null);
		setShowProducerModal(false);
	}, [productPageData.id]);
	const shouldShowProducerLink = store === 'kgde' && !!tcId;

	const producerParams = { tcId };
	const {
		data: producerData,
		error: producerError,
		isLoading: producerLoading,
	} = useApi(shouldShowProducerLink && showProducerModal ? Res.productProducer : null, producerParams);

	const handleCloseProducerModal = () => setShowProducerModal(false);
	const handleOpenProducerModal = () => setShowProducerModal(true);
	const showLinksSection = links.length > 0 || shouldShowProducerLink;

	const renderProducerAddress = (address: string) => {
		const parts = address.split('\n');
		return parts.map((line, index) => (
			<span key={`${line}-${index}`}>
				{line}
				{index < parts.length - 1 && <br />}
			</span>
		));
	};

	return (
		<>
			<ContentCard>
				<div className="links">
					{showLinksSection && (
						<ul className="product-info-list">
							{links.map((link, i) => {
								const linkVideo = youtubeVideo(link.url);
								return (
									<li key={link.url + i}>
										{linkVideo ? (
											<button
												type="button"
												className="product-external-link product-external-link-button"
												onClick={() => setVideo(linkVideo)}
											>
												<Icon shape={play} size={19} />
												<span>{link.title}</span>
											</button>
										) : (
											<ExternalLink className="secondary" href={link.url}>
												{link.title}
											</ExternalLink>
										)}
									</li>
								);
							})}
							{shouldShowProducerLink && (
								<li key="producer-link">
									<button
										type="button"
										className="product-external-link product-external-link-button"
										onClick={handleOpenProducerModal}
									>
										<Icon shape={play} size={19} />
										<span>Hersteller</span>
									</button>
								</li>
							)}
							<hr />
						</ul>
					)}
				</div>
				<div className="details-wrapper">
					{vendorProductId && (
						<p>
							<span className="descriptor">{t.artnr}:</span> {vendorProductId}
						</p>
					)}
					{productPageData.mpn && (
						<p>
							<span className="descriptor">{t.originalNumber}:</span> {productPageData.mpn}
						</p>
					)}
					{productPageData.gtin && (
						<p>
							<span className="descriptor">EAN:</span> {productPageData.gtin}
						</p>
					)}
				</div>
			</ContentCard>
			<Modal
				className="content-card-modal video-modal"
				show={video !== null}
				onDismiss={() => setVideo(null)}
				toggle={() => setVideo(null)}
				disableBodyScroll
			>
				<ContentCard className="content">
					<a className="discard" onClick={() => setVideo(null)}>
						<Icon shape={close} />
					</a>
					{video && (
						// the sheet is the whole screen on a phone, where no overlay is left beside it to
						// catch the tap that dismisses a card on a desktop
						<div
							className="video-modal-sheet"
							onClick={(e) => e.target === e.currentTarget && setVideo(null)}
						>
							<div className="video-modal-player">
								<EmbeddedYoutubeVideo
									videoId={video.id}
									start={video.start}
									nocookie={video.nocookie}
								/>
							</div>
						</div>
					)}
				</ContentCard>
			</Modal>
			{shouldShowProducerLink && (
				<Modal
					className="content-card-modal"
					show={showProducerModal}
					onDismiss={handleCloseProducerModal}
					toggle={handleCloseProducerModal}
				>
					<ContentCard className="content">
						<a className="discard" onClick={handleCloseProducerModal}>
							<Icon shape={close} />
						</a>
						<h2>Hersteller</h2>
						{producerLoading && <Loader />}
						{!producerLoading && producerError && (
							<p className="producer-error">Informationen konnten nicht geladen werden.</p>
						)}
						{!producerLoading && !producerError && producerData?.address && (
							<p className="producer-address">{renderProducerAddress(producerData.address)}</p>
						)}
						{!producerLoading && !producerError && !producerData?.address && (
							<p className="producer-empty">Keine Herstellerinformationen verf&uuml;gbar.</p>
						)}
					</ContentCard>
				</Modal>
			)}
		</>
	);
}
